import React, { useState } from "react";
import { AddFlex, CustomText, DialogHeader, Gap } from "../../reusableStyles";
import { Button, Segmented } from "antd";
import {
  isArrayNullOrUndefined,
  isNullOrUndefined,
  isStringNullOrUndefined,
  isValidURL,
} from "../../utils";
import { MessageOutlined, ProductOutlined } from "@ant-design/icons";
import { primaryActionColor, primaryActionColorLight } from "../../constants";
import useDialogActionsHook from "../../CustomHooks.js/useDialogActionsHook";
import DialogWrapper from "../../DialogWrapper";
import ProductCataloguePopup from "./ProductCataloguePopup";

const AdvancedKeywordResponses = ({
  products,
  keywords,
  closeDialog,
  handleSetAutomationData,
  dmMessage,
}) => {
  // Sample words array - replace with your actual array
  const {
    isDialogOpen,
    openDialog,
    closeDialog: onClose,
  } = useDialogActionsHook();
  const [activeTab, setActiveTab] = useState(0);
  const [error, setError] = useState("");
  const [selectedWord, setSelectedWord] = useState("");
  const [formData, setFormData] = useState(
    dmMessage ||
      keywords.reduce(
        (acc, word) => ({
          ...acc,
          [word.toLowerCase()]: { message: "", link: "", buttonText: "" },
        }),
        {}
      )
  );

  const handleSubmit = () => {
    const responses = Object.values(formData);
    for (let index = 0; index < responses.length; index++) {
      let response = responses[index];
      if (response.type === "products") {
        if (isArrayNullOrUndefined(response.products.data)) {
          setError("Please add atleast one product");
          setActiveTab(index);
          return;
        }
      } else {
        if (isStringNullOrUndefined(response.message)) {
          setError("Please enter a valid message");
          setActiveTab(index);
          return;
        }
        if (!isStringNullOrUndefined(response.link)) {
          if (isNullOrUndefined(response.buttonText)) {
            setError("Please enter a valid button text");
            setActiveTab(index);
            return;
          }
        }
        if (!isStringNullOrUndefined(response.buttonText)) {
          if (
            isStringNullOrUndefined(response.link) ||
            !isValidURL(response.link)
          ) {
            setError("Please enter a valid link");
            setActiveTab(index);
            return;
          }
        }
      }
    }
    setError("");
    handleSetAutomationData("dmMessage", {
      isAdvanced: true,
      data: formData,
    });
    closeDialog();
  };

  const handleInputChange = (word, field, value) => {
    setError("");
    setFormData((prev) => ({
      ...prev,
      [word]: { ...prev[word], [field]: value },
    }));
  };

  console.log(formData);

  return (
    <>
      <AddFlex flexDirection="column" height="100%">
        <DialogHeader
          mainText={"Custom Responses"}
          subText={"Customise your responses according to the keyword!!"}
          onClose={closeDialog}
          padding={"10px 20px"}
        />
        <div className="w-full p-4 space-y-4">
          {/* Tabs */}
          <div className="flex space-x-1">
            {keywords.map((word, index) => (
              <button
                key={word}
                className={`px-4 py-1 text-sm font-medium rounded-full ${
                  activeTab === index
                    ? "bg-blue-100 text-blue-600 border border-blue-200"
                    : "text-gray-500 border border-gray-200 hover:text-gray-700 bg-gray-50"
                }`}
                onClick={() => setActiveTab(index)}
              >
                {word}
              </button>
            ))}
          </div>

          {error && <CustomText color="red">{error}</CustomText>}

          {/* Content area */}
          <div className="space-y-4">
            {keywords.map((keyword, index) => {
              let word = keyword.toLowerCase();
              return (
                <div
                  key={word}
                  className={`space-y-4 ${
                    activeTab === index ? "block" : "hidden"
                  }`}
                >
                  <Segmented
                    shape="round"
                    size="medium"
                    value={formData[word]?.type || "message"}
                    options={[
                      {
                        label: "Message",
                        value: "message",
                        icon: <MessageOutlined />,
                      },
                      {
                        label: "Products",
                        value: "products",
                        icon: <ProductOutlined />,
                      },
                    ]}
                    onChange={(value) => {
                      handleInputChange(word, "type", value);
                      if (value === "products") {
                        setFormData((prev) => ({
                          ...prev,
                          [word]: {
                            type: "products",
                            products: {
                              type: "products",
                              data: [],
                            },
                          },
                        }));
                      } else {
                        setFormData((prev) => ({
                          ...prev,
                          [word]: { message: "", link: "", buttonText: "" },
                        }));
                      }
                    }}
                  />
                  {formData[word]?.type === "products" ? (
                    <AddFlex
                      padding="20px"
                      flexDirection="column"
                      gap="2px"
                      backgroundColor={primaryActionColorLight}
                      border={`2px dashed ${primaryActionColor}`}
                      borderRadius="12px"
                      onClick={() => {
                        setSelectedWord(word);
                        openDialog();
                      }}
                      style={{ cursor: "pointer" }}
                    >
                      <CustomText fontSize="14px" color={primaryActionColor}>
                        Click to Add Products
                      </CustomText>
                      {!isArrayNullOrUndefined(
                        formData[word]?.products.data
                      ) && (
                        <CustomText fontSize="14px" color={primaryActionColor}>
                          {formData[word].products.data.length} Product(s) Added
                        </CustomText>
                      )}
                    </AddFlex>
                  ) : (
                    <>
                      {/* Message input */}
                      <div className="space-y-2">
                        <textarea
                          className="w-full h-32 p-3 border border-gray-200 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Customise your message."
                          value={formData[word]?.message}
                          onChange={(e) =>
                            handleInputChange(word, "message", e.target.value)
                          }
                        />
                      </div>

                      {/* Link input */}
                      <div className="space-y-2">
                        <input
                          type="text"
                          className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Add the link"
                          value={formData[word]?.link}
                          onChange={(e) =>
                            handleInputChange(word, "link", e.target.value)
                          }
                        />
                      </div>

                      {/* Button text input */}
                      <div className="space-y-2">
                        <input
                          type="text"
                          className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Customise the button text."
                          value={formData[word]?.buttonText}
                          onChange={(e) =>
                            handleInputChange(
                              word,
                              "buttonText",
                              e.target.value
                            )
                          }
                        />
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        <Gap />
        <AddFlex
          gap="10px"
          padding="10px 20px"
          style={{ borderTop: "1px solid #ececec" }}
        >
          {activeTab !== 0 && (
            <Button onClick={() => setActiveTab((prev) => prev - 1)}>
              Prev
            </Button>
          )}
          <Gap />
          {keywords.length > 1 && activeTab !== keywords.length - 1 && (
            <Button
              onClick={() => {
                setActiveTab((prev) => prev + 1);
              }}
            >
              Next
            </Button>
          )}
          <Button type="primary" onClick={handleSubmit}>
            Done
          </Button>
        </AddFlex>
      </AddFlex>
      <DialogWrapper
        open={isDialogOpen}
        closeDialog={closeDialog}
        height={"70vh"}
        component={
          <ProductCataloguePopup
            products={products}
            setSelectedProducts={(products) => {
              setFormData((prev) => ({
                ...prev,
                [selectedWord]: {
                  ...prev[selectedWord],
                  products: {
                    ...prev[selectedWord].products,
                    data: products.data,
                  },
                },
              }));
            }}
            selectedProducts={formData[selectedWord]?.products}
            hideCategoryViewOption={true}
            onClose={() => {
              setSelectedWord("");
              onClose();
            }}
          />
        }
      />
    </>
  );
};

export default AdvancedKeywordResponses;
